import { TIMEFRAME_MINUTES, type Candle, type Timeframe } from '@/lib/types'

/**
 * Fait vivre la dernière bougie avec les cotations en direct.
 *
 * Les bougies ne sont chargées qu'une fois, au choix de la paire et de
 * l'intervalle. Sans ce repli des cotations, la dernière restait figée sur sa
 * clôture du chargement : le prix inscrit sur l'échelle de droite ne bougeait
 * plus, alors que la cotation affichée au-dessus, elle, continuait de vivre.
 * Deux prix contradictoires sur le même écran.
 *
 * Renvoie le MÊME tableau quand rien ne change, et sinon un tableau neuf qui
 * partage toutes les bougies précédentes. Le graphique s'en sert pour ne
 * redessiner que la dernière au lieu de tout recharger à chaque cotation.
 */
export function foldQuote(
  candles: Candle[],
  price: number,
  timeframe: Timeframe,
  nowSeconds: number,
): Candle[] {
  const last = candles.at(-1)
  // Sans historique, on n'invente pas de bougie : elle n'aurait ni ouverture
  // réelle ni place dans la série qu'on va charger.
  if (!last || !Number.isFinite(price) || price <= 0) return candles

  const close = Number(last.close)
  // Garde-fou : une cotation à plus de 20 % de la dernière clôture ne peut
  // pas être la même paire. Un saut pareil écraserait l'échelle du graphique
  // en un trait plat — mieux vaut l'ignorer que la dessiner.
  if (close > 0 && Math.abs(price - close) / close > 0.2) return candles

  const step = TIMEFRAME_MINUTES[timeframe] * 60
  /*
   * La bougie suivante commence là où la dernière a commencé, plus un pas —
   * et non à l'heure ronde calculée ici. Le courtier aligne ses bougies de
   * quatre heures et journalières sur son propre fuseau : les recaler sur
   * minuit UTC fabriquerait une bougie en trop à chaque changement de jour.
   */
  const elapsed = nowSeconds - last.time
  if (elapsed < 0) return candles

  const text = String(price)

  if (elapsed < step) {
    const high = Math.max(Number(last.high), price)
    const low = Math.min(Number(last.low), price)
    if (Number(last.close) === price && Number(last.high) === high && Number(last.low) === low) {
      return candles
    }
    const updated: Candle = {
      ...last,
      high: String(high),
      low: String(low),
      close: text,
    }
    return [...candles.slice(0, -1), updated]
  }

  const opened: Candle = {
    time: last.time + Math.floor(elapsed / step) * step,
    open: text,
    high: text,
    low: text,
    close: text,
    volume: 0,
  }
  return [...candles, opened]
}
